import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNeumorphicTheme } from "@/contexts/NeumorphicThemeContext";

interface HeadlineData {
  title: string;
  sub: string;
}

const headlines: HeadlineData[] = [{
  title: "We Don't Build ADUs. We Architect Fortunes.",
  sub: "Elevating backyard spaces into long-term investment vehicles."
}, {
  title: "Where Silicon Valley Innovation Meets Timeless Craftsmanship.",
  sub: "Designed for tech-savvy investors and legacy builders alike."
}, {
  title: "Optimizing Exceptional Properties Into Generational Wealth.",
  sub: "Let your lot build equity across generations."
}, {
  title: "The Only ADU Company That Guarantees Your ROI Before Breaking Ground.",
  sub: "Transforming construction risk into investment certainty."
}, {
  title: "Turning Backyards Into Boardroom Strategies.",
  sub: "Because your property deserves a financial strategy."
}, {
  title: "The Private Equity Approach to ADU Development",
  sub: "We bring investor logic to ADU construction."
}, {
  title: "When Your Property Needs a CFO, Not Just a Contractor",
  sub: "Every ADU plan starts with a financial model."
}, {
  title: "Building California's Next Generation of Landlords",
  sub: "Empowering homeowners to build income, equity, and legacy."
}];

const RotatingHeadline = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  // Try to get theme, provide fallback for pages without provider
  let theme = "dark";
  try {
    const themeContext = useNeumorphicTheme();
    theme = themeContext.theme;
  } catch {
    // Not wrapped in provider, use default
  }

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex(prevIndex => (prevIndex + 1) % headlines.length);
    }, 4000);

    return () => clearInterval(interval);
  }, []);

  const bgColor = theme === "dark" ? "bg-[#0a0a0a]" : "bg-[#f5f5f5]";
  const subTextColor = theme === "dark" ? "text-white/90" : "text-[#1a1a1a]/90";

  return (
    <div className={`w-full ${bgColor} md:py-12 py-[20px] transition-colors duration-500`}>
      <div className="max-w-6xl mx-auto px-4">
        <div className="h-[200px] md:h-[280px] flex items-center justify-center my-0">
          <AnimatePresence mode="wait">
            <motion.div 
              key={currentIndex} 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              exit={{ opacity: 0, y: -20 }} 
              transition={{ duration: 0.6 }} 
              className="text-center"
            >
              <h2 className="text-2xl font-light text-[#E4683B] mb-4 leading-tight md:text-5xl">
                {headlines[currentIndex].title}
              </h2>
              <p className={`text-lg md:text-xl ${subTextColor} leading-relaxed max-w-4xl mx-auto transition-colors duration-500`}>
                {headlines[currentIndex].sub}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default RotatingHeadline;
